import React from 'react';
import { Modal, Table, Progress, Spin, Alert } from 'antd';
import { ChartColumn, CircleCheck, TriangleAlert, CircleX, Store, Gauge } from 'lucide-react';
import { StatCard, StatusTag, EmptyState } from '../../common';
import { semantic } from '../../../theme/tokens';
import { formatNumber } from '../../../utils/format';
import { useSOSAnalysisController, buildSOSRows } from './sosAnalysisModal.controller';

const complianceTone = (compliance) => {
    if (compliance === null || compliance === undefined) return 'neutral';
    if (compliance >= 90) return 'success';
    if (compliance >= 70) return 'warning';
    return 'danger';
};

function SOSAnalysisModal({ visible, tag, onCancel }) {
    const { loading, analysis } = useSOSAnalysisController({ visible, tag });

    const getComplianceIcon = (compliance) => {
        if (compliance === null) return <TriangleAlert />;
        if (compliance >= 90) return <CircleCheck />;
        if (compliance >= 70) return <TriangleAlert />;
        return <CircleX />;
    };

    const columns = [
        {
            title: 'Tienda',
            dataIndex: ['store', 'name'],
            key: 'store',
            width: 200,
            ellipsis: true
        },
        {
            title: 'Tipo',
            dataIndex: ['store', 'storeType'],
            key: 'storeType',
            width: 120,
            render: (type) => <StatusTag tone="neutral">{type}</StatusTag>
        },
        {
            title: 'Última Auditoría',
            dataIndex: 'lastCheckDate',
            key: 'lastCheckDate',
            width: 150,
            render: (date) => (date ? <span className="tabular-nums">{new Date(date).toLocaleDateString()}</span> : <span className="text-surface-400">Sin datos</span>)
        },
        {
            title: 'Cumplimiento',
            dataIndex: 'compliance',
            key: 'compliance',
            width: 170,
            sorter: (a, b) => (a.compliance || 0) - (b.compliance || 0),
            render: (compliance, record) => {
                if (!record.hasData) {
                    return <StatusTag tone="neutral">Sin datos</StatusTag>;
                }
                if (compliance === null) {
                    return <StatusTag tone="neutral">Sin categorías</StatusTag>;
                }
                return (
                    <Progress
                        percent={Math.round(compliance)}
                        size="small"
                        status={compliance >= 70 ? 'success' : 'exception'}
                        strokeColor={compliance >= 90 ? semantic.success.solid : compliance >= 70 ? semantic.warning.solid : semantic.danger.solid}
                    />
                );
            }
        },
        {
            title: 'Estado',
            key: 'status',
            width: 130,
            render: (_, record) => {
                const compliance = record.compliance;
                return (
                    <StatusTag tone={complianceTone(compliance)} icon={getComplianceIcon(compliance)}>
                        {compliance !== null && compliance >= 90 && <span>Excelente</span>}
                        {compliance !== null && compliance >= 70 && compliance < 90 && <span>Aceptable</span>}
                        {compliance !== null && compliance < 70 && <span>Bajo</span>}
                        {compliance === null && <span>Sin datos</span>}
                    </StatusTag>
                );
            }
        }
    ];

    const expandedRowRender = (record) => {
        if (!record.hasData || !record.actualSOS || Object.keys(record.actualSOS).length === 0) {
            return <EmptyState className="!py-6" title="No hay datos de categorías disponibles" />;
        }

        const sosData = buildSOSRows(record);

        const sosColumns = [
            {
                title: 'Categoría',
                dataIndex: 'category',
                key: 'category'
            },
            {
                title: 'Esperado',
                dataIndex: 'expected',
                key: 'expected',
                align: 'right',
                render: (val) => <span className="tabular-nums">{`${val.toFixed(2)}%`}</span>
            },
            {
                title: 'Actual',
                dataIndex: 'actual',
                key: 'actual',
                align: 'right',
                render: (val) => <span className="tabular-nums">{`${val.toFixed(2)}%`}</span>
            },
            {
                title: 'Desviación',
                dataIndex: 'deviation',
                key: 'deviation',
                align: 'right',
                render: (val) => (
                    <StatusTag tone={val < 5 ? 'success' : val < 10 ? 'warning' : 'danger'} className="tabular-nums">
                        {val.toFixed(2)}%
                    </StatusTag>
                )
            }
        ];

        return <Table columns={sosColumns} dataSource={sosData} pagination={false} size="small" rowKey="category" />;
    };

    const avg = analysis?.summary?.averageCompliance;

    return (
        <Modal
            title={
                <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-bg text-[16px] text-violet-fg">
                        <ChartColumn />
                    </span>
                    <span className="min-w-0 truncate text-[15px] font-semibold text-surface-900">Análisis de Share of Shelf: {tag?.name}</span>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={1000}
            destroyOnClose>
            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <Spin size="large" />
                </div>
            ) : analysis ? (
                <div className="mt-4 space-y-4">
                    <Alert
                        message="Objetivos de SOS"
                        description={
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {analysis.tag.sosTargets.map((target) => (
                                    <StatusTag key={target.category} tone="info" className="tabular-nums">
                                        {target.category}: {target.targetPercentage.toFixed(2)}%
                                    </StatusTag>
                                ))}
                            </div>
                        }
                        type="info"
                    />

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <StatCard label="Total de Tiendas" value={formatNumber(analysis.summary.totalStores)} icon={<Store />} tone="brand" />
                        <StatCard label="Tiendas con Datos" value={formatNumber(analysis.summary.storesWithData)} icon={<CircleCheck />} tone="success" valueTone="success" />
                        <StatCard
                            label="Cumplimiento Promedio"
                            value={`${avg?.toFixed(2) || 'N/A'}${avg !== null ? '%' : ''}`}
                            icon={<Gauge />}
                            tone={complianceTone(avg)}
                            valueTone={avg >= 90 ? 'success' : avg >= 70 ? 'warning' : 'danger'}
                        />
                    </div>

                    <div className="overflow-hidden rounded-xl border border-surface-200">
                        <Table
                            columns={columns}
                            dataSource={analysis.stores}
                            rowKey={(record) => record.store._id}
                            size="middle"
                            expandable={{
                                expandedRowRender,
                                rowExpandable: (record) => record.hasData
                            }}
                            pagination={{ pageSize: 10 }}
                        />
                    </div>
                </div>
            ) : (
                <EmptyState icon={<ChartColumn />} title="No hay datos disponibles" />
            )}
        </Modal>
    );
}

export default SOSAnalysisModal;
